import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  LinearProgress,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useState, useRef, useEffect } from "react";
import { ImportOptions, ImportResult, EXPORT_CONSTANTS } from "@/types/export.types";

interface ImportDialogProps {
  open: boolean;
  onClose: () => void;
  onImport: (file: File, options: ImportOptions, onProgress?: (progress: number, total: number) => void) => Promise<ImportResult>;
}

export default function ImportDialog({
  open,
  onClose,
  onImport,
}: ImportDialogProps): JSX.Element {
  const [file, setFile] = useState<File | null>(null);
  const [password, setPassword] = useState("");
  const [overwriteDuplicates, setOverwriteDuplicates] = useState(true);
  const [importValidOnly, setImportValidOnly] = useState(false);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState("");
  const [needsPassword, setNeedsPassword] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  const resetForm = (): void => {
    setFile(null);
    setPassword("");
    setOverwriteDuplicates(true);
    setImportValidOnly(false);
    setImporting(false);
    setProgress(0);
    setTotal(0);
    setError("");
    setNeedsPassword(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      void checkIfEncrypted(selectedFile);
    }
  };

  const checkIfEncrypted = async (selectedFile: File): Promise<void> => {
    const extension = selectedFile.name.split(".").pop()?.toLowerCase();
    if (extension === "qaddr") {
      try {
        const arrayBuffer = await selectedFile.arrayBuffer();
        const bytes = new Uint8Array(arrayBuffer);
        const flags = bytes[5];
        const isEncrypted = flags !== undefined && (flags & EXPORT_CONSTANTS.FLAGS.ENCRYPTED) !== 0;
        setNeedsPassword(isEncrypted);
      } catch {
        setNeedsPassword(false);
      }
    } else {
      setNeedsPassword(false);
    }
  };

  const handleImport = async (): Promise<void> => {
    if (!file) {
      setError("请选择文件");
      return;
    }

    setError("");
    setImporting(true);
    setProgress(0);
    setTotal(0);

    try {
      const options: ImportOptions = {
        password: needsPassword ? password : undefined,
        overwriteDuplicates,
        importValidOnly,
      };

      const result = await onImport(file, options, (prog: number, tot: number) => {
        setProgress(prog);
        setTotal(tot);
      });

      if (result.successCount > 0) {
        handleClose();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "导入失败");
    } finally {
      setImporting(false);
    }
  };

  const handleClose = (): void => {
    resetForm();
    onClose();
  };

  const progressPercentage = total > 0 ? Math.round((progress / total) * 100) : 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>导入地址</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            选择文件
          </Typography>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.qaddr"
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />
          <Button
            variant="outlined"
            component="label"
            htmlFor="file-input"
            onClick={() => fileInputRef.current?.click()}
            fullWidth
          >
            {file ? file.name : "选择文件..."}
          </Button>
          {file && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              文件大小: {(file.size / 1024).toFixed(2)} KB
            </Typography>
          )}
        </Box>

        {needsPassword && (
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="密码"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={importing}
            />
          </Box>
        )}

        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={overwriteDuplicates}
                onChange={(e) => setOverwriteDuplicates(e.target.checked)}
                disabled={importing}
              />
            }
            label="覆盖重复的呼号"
          />
          <Box sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={importValidOnly}
                  onChange={(e) => setImportValidOnly(e.target.checked)}
                  disabled={importing}
                />
              }
              label="仅导入有效地址"
            />
          </Box>
        </Box>

        {importing && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress variant="determinate" value={progressPercentage} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              已处理: {progress} / {total} ({progressPercentage}%)
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={importing}>
          取消
        </Button>
        <Button
          onClick={() => void handleImport()}
          variant="contained"
          disabled={!file || importing || (needsPassword && !password)}
        >
          {importing ? "导入中..." : "导入"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
